import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { employeeServiceApi } from "../api/employeeService";

export const fetchEmployees = createAsyncThunk(
  "employees/fetchEmployees",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { uid } = getState().auth;
      const response = await employeeServiceApi.fetchEmployees(uid);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch employees");
    }
  }
);

export const fetchMyProfile = createAsyncThunk(
  "employees/fetchMyProfile",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { uid } = getState().auth;
      const response = await employeeServiceApi.getMyProfile(uid);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch profile");
    }
  }
);

export const createEmployee = createAsyncThunk(
  "employees/createEmployee",
  async (employeeData, { getState, rejectWithValue }) => {
    try {
      const { uid } = getState().auth;
      const response = await employeeServiceApi.createEmployee(employeeData, uid);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create employee");
    }
  }
);

export const updateEmployee = createAsyncThunk(
  "employees/updateEmployee",
  async ({ id, updates }, { getState, rejectWithValue }) => {
    try {
      const { uid } = getState().auth;
      await employeeServiceApi.updateEmployee(id, updates, uid);
      return { id, updates };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update employee");
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  "employees/deleteEmployee",
  async (id, { getState, rejectWithValue }) => {
    try {
      const { uid } = getState().auth;
      await employeeServiceApi.deleteEmployee(id, uid);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete employee");
    }
  }
);

export const disableEmployee = createAsyncThunk(
  "employees/disableEmployee",
  async (id, { getState, rejectWithValue }) => {
    try {
      const { uid } = getState().auth;
      await employeeServiceApi.disableEmployee(id, uid);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to disable employee");
    }
  }
);

export const enableEmployee = createAsyncThunk(
  "employees/enableEmployee",
  async (id, { getState, rejectWithValue }) => {
    try {
      const { uid } = getState().auth;
      await employeeServiceApi.enableEmployee(id, uid);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to enable employee");
    }
  }
);

const employeeSlice = createSlice({
  name: "employees",
  initialState: {
    employees: [],
    selectedEmployee: null,
    myProfile: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectEmployee: (state, action) => {
      state.selectedEmployee = action.payload;
    },
    clearSelectedEmployee: (state) => {
      state.selectedEmployee = null;
    },
    clearMyProfile: (state) => {
      state.myProfile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.employees.push(action.payload);
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.employees = action.payload;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMyProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.myProfile = action.payload;
      })
      .addCase(fetchMyProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        const index = state.employees.findIndex((emp) => emp.id === action.payload.id);
        if (index !== -1) {
          state.employees[index] = { ...state.employees[index], ...action.payload.updates };
        }
        if (state.selectedEmployee && state.selectedEmployee.id === action.payload.id) {
          state.selectedEmployee = { ...state.selectedEmployee, ...action.payload.updates };
        }
        if (state.myProfile && state.myProfile.id === action.payload.id) {
          state.myProfile = { ...state.myProfile, ...action.payload.updates };
        }
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.employees = state.employees.filter((emp) => emp.id !== action.payload);
        if (state.selectedEmployee && state.selectedEmployee.id === action.payload) {
          state.selectedEmployee = null;
        }
        if (state.myProfile && state.myProfile.id === action.payload) {
          state.myProfile = null;
        }
      })
      .addCase(disableEmployee.fulfilled, (state, action) => {
        const index = state.employees.findIndex((emp) => emp.id === action.payload);
        if (index !== -1) {
          state.employees[index].status = "disabled";
        }
        if (state.selectedEmployee && state.selectedEmployee.id === action.payload) {
          state.selectedEmployee.status = "disabled";
        }
        if (state.myProfile && state.myProfile.id === action.payload) {
          state.myProfile.status = "disabled";
        }
      })
      .addCase(enableEmployee.fulfilled, (state, action) => {
        const index = state.employees.findIndex((emp) => emp.id === action.payload);
        if (index !== -1) {
          state.employees[index].status = "active";
        }
        if (state.selectedEmployee && state.selectedEmployee.id === action.payload) {
          state.selectedEmployee.status = "active";
        }
        if (state.myProfile && state.myProfile.id === action.payload) {
          state.myProfile.status = "active";
        }
      });
  },
});

export const { clearError, selectEmployee, clearSelectedEmployee, clearMyProfile } = employeeSlice.actions;
export default employeeSlice.reducer;
